export const INSTALLMENT_PAYMENT_TYPE = "installment";
export const ONE_TIME_PAYMENT_TYPE = "one_time";

export const BILLING_FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
  { value: "bimonthly", label: "Bimonthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
  { value: "biennial", label: "Biennial" },
];

const BILLING_FREQUENCY_ALIASES = {
  yearly: "annual",
};

const BILLING_FREQUENCY_OPTION_LABELS = BILLING_FREQUENCY_OPTIONS.reduce(
  (labels, option) => ({
    ...labels,
    [option.value]: option.label,
  }),
  {}
);

const BILLING_FREQUENCY_LABELS = {
  weekly: "week",
  biweekly: "2 weeks",
  monthly: "month",
  bimonthly: "2 months",
  quarterly: "quarter",
  annual: "year",
  biennial: "2 years",
};

function resolveBillingFrequency(billingFrequency) {
  return BILLING_FREQUENCY_ALIASES[billingFrequency] || billingFrequency;
}

export function formatCurrencyNumber(value) {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return "0.00";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsedValue);
}

export function getEstimatedInstallmentAmount(packagePrice, numberOfRebills) {
  const totalPrice = Number(packagePrice);
  const installmentCount = Number(numberOfRebills);

  if (!Number.isFinite(totalPrice) || !Number.isFinite(installmentCount) || installmentCount < 1) {
    return null;
  }

  return totalPrice / installmentCount;
}

export function buildPackagePayload(values) {
  const payload = {
    package_type: values.package_type,
    package_name: values.package_name,
    package_desc: values.package_desc,
    package_price: Number(values.package_price),
    session_count: Number(values.session_count),
    currency_code: values.currency_code,
    currency_sign: values.currency_sign,
    payment_type: values.payment_type || ONE_TIME_PAYMENT_TYPE,
  };

  if (payload.payment_type === INSTALLMENT_PAYMENT_TYPE) {
    payload.billing_frequency = values.billing_frequency;
    payload.number_of_rebills = Number(values.number_of_rebills);
  }

  return payload;
}

export function getBillingFrequencySummaryUnit(billingFrequency) {
  return BILLING_FREQUENCY_LABELS[resolveBillingFrequency(billingFrequency)] || "billing period";
}

export function getBillingFrequencyLabel(billingFrequency) {
  return BILLING_FREQUENCY_OPTION_LABELS[resolveBillingFrequency(billingFrequency)] || "Billing period";
}

export function normalizeBillingFrequency(billingFrequency) {
  return resolveBillingFrequency(billingFrequency);
}

export function getInstallmentSummary(packageData) {
  if (packageData?.payment_type !== INSTALLMENT_PAYMENT_TYPE) {
    return null;
  }

  const billingFrequency = resolveBillingFrequency(packageData?.billing_frequency);
  const frequencyLabel = getBillingFrequencyLabel(billingFrequency).toLowerCase();
  const numberOfRebills = Number(packageData?.number_of_rebills);
  const packagePrice = Number(packageData?.package_price);
  const fallbackInstallmentAmount = getEstimatedInstallmentAmount(
    packagePrice,
    numberOfRebills
  );
  const perInstallmentAmount = Number.isFinite(Number(packageData?.per_installment_amount))
    ? Number(packageData?.per_installment_amount)
    : fallbackInstallmentAmount;

  if (
    !billingFrequency ||
    !Number.isFinite(numberOfRebills) ||
    numberOfRebills < 1
  ) {
    return null;
  }

  const currencySign = packageData?.currency_sign || "";
  const installmentAmount = `${currencySign}${formatCurrencyNumber(
    perInstallmentAmount
  )}`;
  const totalAmount = `${currencySign}${formatCurrencyNumber(packagePrice)}`;
  const remainingInstallments = Math.max(numberOfRebills - 1, 0);

  if (remainingInstallments === 0) {
    return `Immediate charge of ${installmentAmount}. Total paid: ${totalAmount}.`;
  }

  return `Immediate charge of ${installmentAmount}, then ${remainingInstallments} ${frequencyLabel} payment${
    remainingInstallments === 1 ? "" : "s"
  } of ${installmentAmount}. Total paid: ${totalAmount}.`;
}
